import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Medal,
  TrendUp,
  Trophy,
  Warning,
  X,
  XCircle,
  type Icon,
} from "@phosphor-icons/react";
import { format } from "date-fns";
import { GoalStatus } from "hooks/api/documentGeneration/useGetDocumentChecklist";
import GoalProgressCard from "./goals/GoalProgressCard";
import { getNotIncludedReason, isGoalNotIncluded } from "./goalInclusion";
import AdditionalInformationSection, {
  AdditionalAttachment,
} from "./AdditionalInformationSection";
import OverrideChip from "./OverrideChip";

const formatStatusDate = (iso?: string | null): string =>
  iso ? format(new Date(iso), "M/d/yyyy") : "";

const captionForStatusChange = (label: string, iso?: string | null): string => {
  const date = formatStatusDate(iso);
  return date ? `${label} · ${date}` : label;
};

interface ReviewReassessmentGoalsStepProps {
  /** Whether an active override waives this document's readiness checks */
  isOverridden?: boolean;
  /** Display title of the document type */
  documentTitle: string;
  /** Client display name shown in the subtitle */
  clientName: string;
  /** Client ID — used for the "Go to" deep link into the Goals & Targets page */
  clientId: string;
  /** Full goal list for the document */
  goals: GoalStatus[];
  /** Whether the goals are still loading */
  isLoading: boolean;
  /** When true, block advancing unless at least one goal will be included/sent to MIRA. */
  requiresAtLeastOneGoal: boolean;
  /** Progress statement text per goalId (owned by the dialog so it survives navigation). */
  progressStatements: Record<string | number, string>;
  /** Called when a goal's progress statement changes. */
  onProgressStatementChange: (goalId: string | number, value: string) => void;
  /** Per-target chart-image inclusion. Undefined = included (default). */
  selectedTargetIds: Record<string | number, boolean>;
  /** Called when a target's chart-image inclusion is toggled. */
  onTargetSelectionChange: (targetId: string | number, selected: boolean) => void;
  /** Registers a chart instance with the dialog so it can be snapshotted at generate time. */
  registerChart: (
    goalId: string | number,
    kind: "cumulative" | "target",
    targetId: string | number | null,
    chart: import("chart.js").Chart<"line"> | import("chart.js").Chart<"bar"> | null
  ) => void;
  /** Callback to return to the previous step */
  onBack: () => void;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Callback when the user confirms the goal selections */
  onContinue: () => void;
  /** Free-text additional information to embed in the generated document */
  additionalInformation: string;
  /** Handler for additional information text changes */
  onAdditionalInformationChange: (value: string) => void;
  /** Image attachments to embed in the generated document */
  additionalFiles: AdditionalAttachment[];
  /** Handler for attachment list changes */
  onAdditionalFilesChange: (files: AdditionalAttachment[]) => void;
}

interface SectionHeaderProps {
  icon: Icon;
  iconColor: string;
  title: string;
  count: number;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  icon: SectionIcon,
  iconColor,
  title,
  count,
}) => (
  <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 3 }}>
    <Box component="span" sx={{ color: iconColor, display: "inline-flex" }}>
      <SectionIcon size={20} weight="duotone" />
    </Box>
    {title} ({count})
  </Typography>
);

/**
 * Review Clinical Goals step for the Reassessment Treatment Plan.
 * Splits goals into In-progress / New / Mastered / Discontinued sections based on `status`,
 * and renders per-goal progress cards (with charts) for the non-"new" sections.
 *
 * @param props
 * @param props.documentTitle - Display title of the document type
 * @param props.clientName - Client display name
 * @param props.goals - Full goal list for the document
 * @param props.isLoading - Whether the goals are still loading
 * @param props.onBack - Callback to return to the previous step
 * @param props.onClose - Callback to close the dialog
 * @param props.onContinue - Callback when the user confirms the goal selections
 */
const ReviewReassessmentGoalsStep: React.FC<ReviewReassessmentGoalsStepProps> = ({
  clientName,
  clientId,
  goals,
  isLoading,
  requiresAtLeastOneGoal,
  progressStatements,
  onProgressStatementChange,
  selectedTargetIds,
  onTargetSelectionChange,
  registerChart,
  onBack,
  onClose,
  onContinue,
  additionalInformation,
  onAdditionalInformationChange,
  additionalFiles,
  onAdditionalFilesChange,
  isOverridden = false,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 11 }}>
        <CircularProgress />
      </Box>
    );
  }

  const includedGoals = goals.filter((g) => !isGoalNotIncluded(g, "withProgress"));
  const blockNoGoals = requiresAtLeastOneGoal && includedGoals.length === 0;
  const notIncludedGoals = goals.filter((g) => isGoalNotIncluded(g, "withProgress"));
  const inProgress = includedGoals.filter((g) => g.status === "in-progress");
  const newGoals = includedGoals.filter((g) => g.status === "new");
  const mastered = includedGoals.filter((g) => g.status === "met");
  const discontinued = includedGoals.filter(
    (g) => g.status === "discontinued" || g.status === "on-hold"
  );

  return (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 7,
          py: 5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={onBack}
            aria-label="Back to review assessments"
            sx={{
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: "10px",
              boxShadow: 1,
              p: "5px",
            }}
          >
            <ArrowLeft size={20} />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h2">
              Review Clinical Goals
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review goals and configure how progress will be presented for {clientName}.
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} aria-label="Close dialog" size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 7, pb: 7, pt: 0 }}>
        <Stack sx={{ mt: 7, gap: 6 }}>
          {isOverridden && <OverrideChip />}
          {blockNoGoals && (
            <Alert severity="warning">
              At least one goal must be included to generate this document.
            </Alert>
          )}
          {inProgress.length > 0 && (
            <Stack sx={{ gap: 3 }}>
              <SectionHeader
                icon={TrendUp}
                iconColor="primary.main"
                title="In-progress goals"
                count={inProgress.length}
              />
              <Stack sx={{ gap: 3 }}>
                {inProgress.map((goal) => (
                  <GoalProgressCard
                    key={goal.id}
                    goal={goal}
                    progressStatement={progressStatements[goal.id] ?? ""}
                    onProgressStatementChange={(value) =>
                      onProgressStatementChange(goal.id, value)
                    }
                    selectedTargetIds={selectedTargetIds}
                    onTargetSelectionChange={onTargetSelectionChange}
                    registerChart={registerChart}
                  />
                ))}
              </Stack>
            </Stack>
          )}

          {newGoals.length > 0 && (
            <Stack sx={{ gap: 3 }}>
              <SectionHeader
                icon={Trophy}
                iconColor="info.main"
                title="New goals"
                count={newGoals.length}
              />
              <Stack sx={{ gap: 3 }}>
                {newGoals.map((goal) => (
                  <GoalProgressCard
                    key={goal.id}
                    goal={goal}
                    caption={goal.goalType}
                    progressStatement={progressStatements[goal.id] ?? ""}
                    onProgressStatementChange={(value) =>
                      onProgressStatementChange(goal.id, value)
                    }
                    selectedTargetIds={selectedTargetIds}
                    onTargetSelectionChange={onTargetSelectionChange}
                    registerChart={registerChart}
                  />
                ))}
              </Stack>
            </Stack>
          )}

          {mastered.length > 0 && (
            <Stack sx={{ gap: 3 }}>
              <SectionHeader
                icon={Medal}
                iconColor="success.main"
                title="Mastered goals"
                count={mastered.length}
              />
              <Stack sx={{ gap: 3 }}>
                {mastered.map((goal) => (
                  <GoalProgressCard
                    key={goal.id}
                    goal={goal}
                    caption={captionForStatusChange("Mastered", goal.statusChangedAt)}
                    progressStatement={progressStatements[goal.id] ?? ""}
                    onProgressStatementChange={(value) =>
                      onProgressStatementChange(goal.id, value)
                    }
                    selectedTargetIds={selectedTargetIds}
                    onTargetSelectionChange={onTargetSelectionChange}
                    registerChart={registerChart}
                  />
                ))}
              </Stack>
            </Stack>
          )}

          {discontinued.length > 0 && (
            <Stack sx={{ gap: 3 }}>
              <SectionHeader
                icon={XCircle}
                iconColor="text.primary"
                title="Discontinued & on-hold"
                count={discontinued.length}
              />
              <Stack sx={{ gap: 3 }}>
                {discontinued.map((goal) => (
                  <GoalProgressCard
                    key={goal.id}
                    goal={goal}
                    caption={captionForStatusChange(
                      goal.status === "on-hold" ? "On hold" : "Discontinued",
                      goal.statusChangedAt
                    )}
                    progressStatement={progressStatements[goal.id] ?? ""}
                    onProgressStatementChange={(value) =>
                      onProgressStatementChange(goal.id, value)
                    }
                    selectedTargetIds={selectedTargetIds}
                    onTargetSelectionChange={onTargetSelectionChange}
                    registerChart={registerChart}
                  />
                ))}
              </Stack>
            </Stack>
          )}

          {notIncludedGoals.length > 0 && (
            <Stack sx={{ gap: 3 }}>
              <SectionHeader
                icon={Warning}
                iconColor="warning.main"
                title="Goals not included"
                count={notIncludedGoals.length}
              />
              <Stack sx={{ gap: 3 }}>
                {notIncludedGoals.map((goal) => {
                  const reason = getNotIncludedReason(goal, "withProgress");
                  return (
                    <Stack
                      key={goal.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "12px",
                        px: 5,
                        py: 4,
                        gap: 3,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" fontWeight={500}>
                          {goal.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {reason?.description}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        color="secondary"
                        endIcon={<ArrowUpRight size={16} />}
                        onClick={() =>
                          window.open(
                            `/clients/${clientId}/goals-targets`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                        sx={{ flexShrink: 0 }}
                      >
                        {reason?.buttonText}
                      </Button>
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          )}

          <AdditionalInformationSection
            additionalInformation={additionalInformation}
            onAdditionalInformationChange={onAdditionalInformationChange}
            files={additionalFiles}
            onFilesChange={onAdditionalFilesChange}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 7, pt: 5, pb: 5 }}>
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowRight size={16} />}
          onClick={onContinue}
          disabled={blockNoGoals && !isOverridden}
        >
          Continue To Confirm
        </Button>
      </DialogActions>
    </>
  );
};

export default ReviewReassessmentGoalsStep;
